// Copyright (c) 2025 Audere, All Rights Reserved

import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";

import TwoLineTest from "../../assets/two_line_test.png";
import ThreeLineTest from "../../assets/three_line_test.png";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import {
  TEST_TYPES,
  type AddNewTestFormProps,
  type LineTestType,
  type addRDTPayload,
} from "../types/projects";
import { isValidString } from "../utils/string";

const AddNewTestForm = ({ lineTestType, setLineTestType, onCancel }: AddNewTestFormProps) => {
  const { t } = useTranslation("AddNewTest");

  const methods = useFormContext<addRDTPayload>();
  const {
    control,
    setValue,
    clearErrors,
    formState: { isValid },
  } = methods;
  const renderInput = (
    name: keyof addRDTPayload,
    label: string,
    placeholder: string,
    rule: string
  ) => (
    <Box sx={styles.formGroup}>
      <FormLabel sx={{ mb: 1 }}>
        {label}{" "}
        <Typography component="span" color="error">
          *
        </Typography>
      </FormLabel>
      <Controller
        name={name}
        control={control}
        rules={{
          required: rule,
          validate: value => isValidString(value) || rule,
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            variant="outlined"
            size="small"
            placeholder={placeholder}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            data-testid={`${name}-input`}
          />
        )}
      />
    </Box>
  );

  const renderLineTestInputs = (type: string) => {
    const isTwoLineTest = type === TEST_TYPES.TWO_LINE;

    return (
      <>
        <Box sx={styles.formGroup}>
          <Box sx={{ my: 2 }}>
            <img
              src={isTwoLineTest ? TwoLineTest : ThreeLineTest}
              alt={t("lineTestImageAlt", { type })}
              width={200}
            />
          </Box>
        </Box>
        {!isTwoLineTest && (
          <Box sx={styles.formGroup}>
            <FormLabel sx={{ mb: 1 }}>
              {t("test1Label")}{" "}
              <Typography component="span" color="error">
                *
              </Typography>
            </FormLabel>
            <Controller
              name="results.window0.test1"
              control={control}
              rules={{
                required: t("test1Required"),
                validate: value => isValidString(value) || t("test1Required"),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="e.g. pan"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>
        )}
        <Box sx={{ ...styles.formGroup, display: "block" }}>
          <FormLabel sx={{ mb: 1 }}>
            {t("test0Label")}{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </FormLabel>
          <Controller
            name="results.window0.test0"
            control={control}
            rules={{
              required: t("test0Required"),
              validate: value => isValidString(value) || t("test0Required"),
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g. pf"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>
      </>
    );
  };

  return (
    <Box data-testid="new-test-form">
      <Box sx={styles.inputSection}>
        {renderInput(
          "brand",
          t("brandNameLabel"),
          t("brandNamePlaceholder"),
          t("brandNameRequired")
        )}
        {renderInput("model", t("modelLabel"), t("modelPlaceholder"), t("modelRequired"))}
        {renderInput(
          "condition",
          t("conditionLabel"),
          t("conditionPlaceholder"),
          t("conditionRequired")
        )}
        <Box sx={styles.formGroup}>
          <FormLabel sx={{ mb: 1 }}>
            {t("lineLabel")}{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </FormLabel>
          <Controller
            name="lineTestType"
            control={control}
            rules={{ required: t("testTypeRequired") }}
            render={({ field, fieldState }) => (
              <Box>
                <RadioGroup
                  {...field}
                  value={lineTestType}
                  onChange={e => {
                    field.onChange(e);
                    setLineTestType(e.target.value as LineTestType);
                    setValue("results.window0.test0", "");
                    setValue("results.window0.test1", "");
                    clearErrors(["results.window0.test0", "results.window0.test1"]);
                  }}
                >
                  {[TEST_TYPES.TWO_LINE, TEST_TYPES.THREE_LINE].map(type => (
                    <Box key={type}>
                      <FormControlLabel value={type} control={<Radio />} label={type} />
                      {lineTestType === type && renderLineTestInputs(type)}
                    </Box>
                  ))}
                </RadioGroup>
                {fieldState.error && (
                  <Typography color="error" variant="caption">
                    {fieldState.error.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Box>
      </Box>
      <Box sx={styles.formButton}>
        <Button type="submit" variant="outlined" onClick={onCancel}>
          {t("common:cancel")}
        </Button>
        <Button type="submit" variant="contained" disabled={!isValid}>
          {t("continueText")}
        </Button>
      </Box>
    </Box>
  );
};

export default AddNewTestForm;

const styles = createStyleSheet({
  formGroup: {
    marginBottom: "20px",
    color: theme.palette.text.secondary,
  },
  inputSection: {
    marginBottom: "20px",
  },
  formButton: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 2,
  },
});
